import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {parseAxiosPromise} from '@/axiosInstance'
import {httpGetUserSpecializations, HttpGetUserSpecializationsOptions} from '@/features/users/http/users.http'
import {z} from 'zod'
import {UserSpecialization} from '../types'
import {useInputSearch} from '@/hooks/useInputSearch'
import {PaginationResponse} from '@/types'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

export const HttpGetSpecializationsResponse = z.object({
    data: z.array(UserSpecialization),
    meta: PaginationResponse
})
export type HttpGetSpecializationsResponse = z.infer<typeof HttpGetSpecializationsResponse>

export const useGetUserSpecializations = (
    promiseArgs: Omit<HttpGetUserSpecializationsOptions, 'page'>,
    options?: Pick<UseInfiniteQueryOptions<HttpGetSpecializationsResponse>, 'enabled'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.USER_SPECIALIZATIONS, searchValue, promiseArgs],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetUserSpecializations({
                    page: pageParam,
                    search: searchValue ?? promiseArgs.search,
                    limit: promiseArgs.limit
                }),
                responseShape: HttpGetSpecializationsResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
